import * as React from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { EmptyMessage, Load, LoadCenter } from 'ui/styled';
import CustomTablePagination from 'components/CustomTablePagination';
import { SortDirection, TabledHeaderButton, TabledRowText } from './styled';
import useI18n from 'hooks/useI18n';

const StyledTable = styled(Table)({
  tableLayout: 'auto',
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: '4px',
});

const StyledTableCell = styled(TableCell, {
  shouldForwardProp: prop => prop !== 'analytical',
})(({ analytical, theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: analytical ? theme.palette.colors.surfaceElevatedStrong : theme.palette.colors.backgroundgrey,
    color: analytical ? theme.palette.colors.muted : undefined,
    whiteSpace: 'nowrap',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    fontFamily: 'Lato',
    padding: '8px 12px',

  },
  [`&.${tableCellClasses.body}`]: {
    borderBottomColor: analytical ? theme.palette.colors.borderSoft : undefined,
    color: analytical ? theme.palette.colors.text : undefined,
    whiteSpace: 'nowrap',
    fontSize: 14,
    fontWeight: 400,
    fontFamily: 'Lato',
    padding: '8px 12px',
  },
}));

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: prop => !['analytical', 'interactive'].includes(prop),
})(({ analytical, interactive, theme }) => ({
  cursor: interactive ? 'pointer' : undefined,
  '&:nth-of-type(odd)': {
    backgroundColor: analytical ? theme.palette.colors.surfaceElevatedSoft : theme.palette.colors.lightshadow,
  },
  '&:nth-of-type(even)': {
    backgroundColor: analytical ? 'transparent' : theme.palette.colors.light,
  },
}));

export default function BasicTable({
  analytical,
  columns = [],
  direction,
  loading,
  minWidth = 900,
  onPageChange,
  onRowClick,
  onSort,
  page: controlledPage,
  pagination,
  rows = [],
  rowsPerPage = 10,
  sort,
  total,
}) {
  const [internalPage, setInternalPage] = React.useState(0);
  const page = typeof controlledPage === 'number' ? Math.max(0, controlledPage - 1) : internalPage;

  const { t } = useI18n()

  const handleChangePage = (event, newPage) => {
    if (typeof controlledPage !== 'number') setInternalPage(newPage);
    if (onPageChange) onPageChange(newPage + 1);
  };
  return (

    <>
      <TableContainer component={Paper} sx={{ background: 'transparent', boxShadow: 'none' }}>
        <StyledTable aria-label="simple table" sx={{ minWidth }}>
          <TableHead>
            <TableRow>
              {columns?.map((item, key) => (
                <StyledTableCell analytical={analytical} key={key} align="left">
                  <TabledRowText>
                    {!item?.sortable ? item.title : (
                      <TabledHeaderButton onClick={() => onSort?.(item.ref)} type="button">
                        {item.title}
                        <SortDirection active={sort === item.ref} descending={sort === item.ref && direction === 'desc'}>⌄</SortDirection>
                      </TabledHeaderButton>
                    )}
                  </TabledRowText>
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(pagination && typeof controlledPage !== 'number' && rowsPerPage > 0
              ? rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
              : rows
            ).map((row, rowIndex) => (
              <StyledTableRow analytical={analytical} interactive={Boolean(onRowClick)} key={row?.id || row?.key || rowIndex} onClick={() => onRowClick?.(row)}>
                {columns?.map((item, colIndex) => (
                  <StyledTableCell analytical={analytical} key={colIndex} align="left">
                    {item.renderCell ? item.renderCell({ row }) : row[item.ref]}
                  </StyledTableCell>
                ))}
              </StyledTableRow>
            ))}
          </TableBody>
        </StyledTable>
        {!loading ? (
          rows?.length ? null : <EmptyMessage>{ t('no_records_found') }</EmptyMessage>
        ) : (
          <LoadCenter>
            <Load />
          </LoadCenter>
        )}

      </TableContainer>
      {!pagination ? null : (
        <>
          <CustomTablePagination
            component="div"
            count={Number(total ?? rows.length)}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
          />
        </>
      )}
    </>
  );
}
